import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { useLiveMatch } from '../LiveMatchContext.tsx';
import { FieldPosition, LivePlayer, TeamSide } from '../liveMatchTypes.ts';

interface Props {
  onBack: () => void;
  onConfirm: () => void;
}

// pos 4 3 2   (rete)
// pos 5 6 1   (fondo)
const FIELD_LAYOUT: number[][] = [
  [4, 3, 2],
  [5, 6, 1],
];

const ROTATION_SCHEMA: Record<number, Record<number, string>> = {
  1: { 1: 'PALLEGGIO', 2: 'BANDA',     3: 'CENTRALE', 4: 'OPPOSTO',   5: 'BANDA',     6: 'CENTRALE' },
  2: { 1: 'CENTRALE',  2: 'PALLEGGIO', 3: 'BANDA',    4: 'CENTRALE',  5: 'OPPOSTO',   6: 'BANDA'    },
  3: { 1: 'BANDA',     2: 'CENTRALE',  3: 'PALLEGGIO', 4: 'BANDA',    5: 'CENTRALE',  6: 'OPPOSTO'  },
  4: { 1: 'OPPOSTO',   2: 'BANDA',     3: 'CENTRALE', 4: 'PALLEGGIO', 5: 'BANDA',     6: 'CENTRALE' },
  5: { 1: 'CENTRALE',  2: 'OPPOSTO',   3: 'BANDA',    4: 'CENTRALE',  5: 'PALLEGGIO', 6: 'BANDA'    },
  6: { 1: 'BANDA',     2: 'CENTRALE',  3: 'OPPOSTO',  4: 'BANDA',     5: 'CENTRALE',  6: 'PALLEGGIO'},
};

const LIBERO_POSITIONS = new Set([1, 5, 6]);

function computeBench(allCallup: LivePlayer[], currentLineup: FieldPosition[]): number[] {
  const inField = new Set(currentLineup.map((fp) => fp.playerId));
  return allCallup.filter((p) => !inField.has(p.id)).map((p) => p.id);
}

export default function LineupSetupStep({ onBack, onConfirm }: Props) {
  const { state, dispatch } = useLiveMatch();
  const side: TeamSide = state.lineupSetupSide;

  const callup  = side === 'HOME' ? state.homeCallup  : state.awayCallup;
  const lineup  = side === 'HOME' ? state.homeLineup  : state.awayLineup;
  const shirtColor = side === 'HOME' ? state.homeShirtColor : state.awayShirtColor;
  const teamName   = side === 'HOME' ? state.homeTeamName   : state.awayTeamName;

  // ─── Stato locale ─────────────────────────────────────────────────────────
  const [selected, setSelected]               = useState<LivePlayer | null>(null);
  const [schema, setSchema]                   = useState<number | null>(null);
  const [liberoError, setLiberoError]         = useState(false);
  const [designatedLiberoId, setDesignatedLiberoId] = useState<number | null>(
    () => lineup.find((fp) => fp.isLibero)?.playerId ?? null,
  );

  // Barra inserimento rapido
  const [quickInputs, setQuickInputs] = useState<string[]>(Array(6).fill(''));
  const [quickErrors, setQuickErrors] = useState<(string | null)[]>(Array(6).fill(null));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  // ─── Reset al cambio di lato ───────────────────────────────────────────────
  useEffect(() => {
    setSelected(null);
    setSchema(null);
    setLiberoError(false);
    setDesignatedLiberoId(lineup.find((fp) => fp.isLibero)?.playerId ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);

  // ─── Sincronizza quickInputs dalla lineup (modalità visiva → barra) ────────
  useEffect(() => {
    const posMap: Record<number, FieldPosition> = {};
    lineup.forEach((fp) => { posMap[fp.position] = fp; });
    setQuickInputs(Array.from({ length: 6 }, (_, i) => {
      const fp = posMap[i + 1];
      if (!fp) return '';
      const player = callup.find((p) => p.id === fp.playerId);
      return player?.shirtNumber ? String(player.shirtNumber) : '';
    }));
    setQuickErrors(Array(6).fill(null));
  }, [lineup, callup]);

  // ─── Mappa e set derivati ──────────────────────────────────────────────────
  const positionMap: Record<number, FieldPosition> = {};
  lineup.forEach((fp) => { positionMap[fp.position] = fp; });
  const assignedIds = new Set(lineup.map((fp) => fp.playerId));
  const available   = callup.filter((p) => !assignedIds.has(p.id));

  // ─── Assegna player a posizione (logica condivisa tra modalità) ────────────
  function assignToPosition(player: LivePlayer, pos: number, allowLiberoWarning = true) {
    const isThisLibero = player.id === designatedLiberoId;

    if (isThisLibero && !LIBERO_POSITIONS.has(pos) && allowLiberoWarning) {
      setLiberoError(true);
      setTimeout(() => setLiberoError(false), 2500);
      // In modalità rapida lascia passare (solo avviso); in modalità visiva blocca
      if (allowLiberoWarning === false) return false;
    }

    const role = isThisLibero ? 'LIBERO' : (schema ? ROTATION_SCHEMA[schema][pos] : 'BANDA');
    const newFp: FieldPosition = { playerId: player.id, position: pos, role, isLibero: isThisLibero };
    let newLineup = lineup.filter((fp) => fp.position !== pos && fp.playerId !== player.id);
    newLineup = [...newLineup, newFp];
    dispatch({ type: 'SET_LINEUP', side, lineup: newLineup, bench: computeBench(callup, newLineup) });
    return true;
  }

  // ─── Click su posizione (modalità visiva) ─────────────────────────────────
  function handlePositionClick(pos: number) {
    if (!selected) return;
    const isThisLibero = selected.id === designatedLiberoId;
    if (isThisLibero && !LIBERO_POSITIONS.has(pos)) {
      setLiberoError(true);
      setTimeout(() => setLiberoError(false), 2500);
      return; // blocca in modalità visiva
    }
    assignToPosition(selected, pos, false);
    setSelected(null);
  }

  // ─── Rimozione dal campo ───────────────────────────────────────────────────
  function handleRemove(pos: number, e: React.MouseEvent) {
    e.stopPropagation();
    const newLineup = lineup.filter((fp) => fp.position !== pos);
    dispatch({ type: 'SET_LINEUP', side, lineup: newLineup, bench: computeBench(callup, newLineup) });
    setSelected(null);
  }

  // ─── Barra inserimento rapido ──────────────────────────────────────────────
  function commitQuickInput(posIndex: number, rawValue: string) {
    const pos    = posIndex + 1;
    const trimmed = rawValue.trim();

    if (!trimmed) {
      // Campo svuotato → rimuovi giocatore da quella posizione
      if (positionMap[pos]) {
        const newLineup = lineup.filter((fp) => fp.position !== pos);
        dispatch({ type: 'SET_LINEUP', side, lineup: newLineup, bench: computeBench(callup, newLineup) });
      }
      setQuickErrors((prev) => { const n = [...prev]; n[posIndex] = null; return n; });
      return;
    }

    const num = parseInt(trimmed, 10);
    if (isNaN(num)) {
      setQuickErrors((prev) => { const n = [...prev]; n[posIndex] = 'Numero non valido'; return n; });
      return;
    }

    const player = callup.find((p) => p.shirtNumber === num);
    if (!player) {
      setQuickErrors((prev) => { const n = [...prev]; n[posIndex] = 'Non in distinta'; return n; });
      return;
    }

    // Assegna (in modalità rapida avvisa ma non blocca per il libero fuori zona)
    assignToPosition(player, pos, true);
    setQuickErrors((prev) => { const n = [...prev]; n[posIndex] = null; return n; });
  }

  function handleQuickKeyDown(e: React.KeyboardEvent, posIndex: number) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitQuickInput(posIndex, quickInputs[posIndex]);
      const next = inputRefs.current[posIndex + 1];
      if (next) next.focus();
    }
  }

  // ─── Schema P1-P6 ──────────────────────────────────────────────────────────
  function handleSchemaChange(_: React.MouseEvent, val: number | null) {
    if (val === null) return;
    setSchema(val);
    const newLineup = lineup.map((fp) =>
      fp.isLibero ? fp : { ...fp, role: ROTATION_SCHEMA[val][fp.position] },
    );
    dispatch({ type: 'SET_LINEUP', side, lineup: newLineup, bench: computeBench(callup, newLineup) });
  }

  // ─── Toggle libero ─────────────────────────────────────────────────────────
  function toggleLibero(player: LivePlayer, e: React.MouseEvent) {
    e.stopPropagation();
    const isAlreadyLibero = player.id === designatedLiberoId;
    if (isAlreadyLibero) {
      setDesignatedLiberoId(null);
      dispatch({ type: 'SET_LIBERO_ID', side, playerId: null });
      if (assignedIds.has(player.id)) {
        const newLineup = lineup.map((fp) =>
          fp.playerId === player.id
            ? { ...fp, isLibero: false, role: schema ? ROTATION_SCHEMA[schema][fp.position] : 'BANDA' }
            : fp,
        );
        dispatch({ type: 'SET_LINEUP', side, lineup: newLineup, bench: computeBench(callup, newLineup) });
      }
    } else {
      setDesignatedLiberoId(player.id);
      dispatch({ type: 'SET_LIBERO_ID', side, playerId: player.id });
      const newLineup = lineup.map((fp) => {
        if (fp.isLibero && fp.playerId !== player.id)
          return { ...fp, isLibero: false, role: schema ? ROTATION_SCHEMA[schema][fp.position] : 'BANDA' };
        if (fp.playerId === player.id) {
          if (!LIBERO_POSITIONS.has(fp.position)) {
            setLiberoError(true);
            setTimeout(() => setLiberoError(false), 2500);
            return fp;
          }
          return { ...fp, isLibero: true, role: 'LIBERO' };
        }
        return fp;
      });
      dispatch({ type: 'SET_LINEUP', side, lineup: newLineup, bench: computeBench(callup, newLineup) });
    }
  }

  // ─── Rotazione antioraria formazione ──────────────────────────────────────
  const CW: Record<number, number> = { 1: 6, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5 };

  function handleRotate() {
    if (!schema) return;
    const newLineup = lineup.map((fp) => {
      const newPos  = CW[fp.position];
      const newRole = fp.isLibero ? 'LIBERO' : ROTATION_SCHEMA[schema][newPos];
      return { ...fp, position: newPos, role: newRole };
    });
    dispatch({ type: 'SET_LINEUP', side, lineup: newLineup, bench: computeBench(callup, newLineup) });
  }

  // ─── Copia da set precedente ───────────────────────────────────────────────
  function copyFromSet(s: typeof state.completedSets[0]) {
    const prevLineup = (side === 'HOME' ? s.homeLineup : s.awayLineup) ?? [];
    const valid = prevLineup.filter((fp) => callup.some((p) => p.id === fp.playerId));
    dispatch({ type: 'SET_LINEUP', side, lineup: valid, bench: computeBench(callup, valid) });
    const palleggiatore = valid.find((fp) => fp.role === 'PALLEGGIO');
    if (palleggiatore) setSchema(palleggiatore.position);
  }

  // ─── Completamento ─────────────────────────────────────────────────────────
  const isComplete = lineup.length === 6 && schema !== null;
  const nextSide: TeamSide     = side === 'HOME' ? 'AWAY' : 'HOME';
  const nextTeamName            = nextSide === 'HOME' ? state.homeTeamName : state.awayTeamName;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, pb: 0 }}>
      <Typography variant="h6" mb={0.5}>Formazione: {teamName}</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Inserisci i numeri di maglia in sequenza (pos 1→6) oppure tocca un giocatore e poi la posizione in campo.
      </Typography>

      {/* ── Copia da set precedente (set 2+) ── */}
      {state.currentSet > 1 && state.completedSets.length > 0 && (
        <Box sx={{ mb: 2, p: 1.25, borderRadius: 1, border: '1px solid', borderColor: 'info.light', bgcolor: 'info.50' }}>
          <Typography variant="subtitle2" color="info.dark" mb={0.75}>
            Copia formazione dal set precedente:
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {state.completedSets.map((s) => (
              <Chip
                key={s.setNumber}
                label={`Set ${s.setNumber}  ${s.homePoints}–${s.awayPoints}`}
                size="small"
                color="info"
                variant="outlined"
                onClick={() => copyFromSet(s)}
                sx={{ cursor: 'pointer', fontWeight: 600 }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {liberoError && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Il libero può giocare solo nelle zone difensive (1, 5, 6).
        </Alert>
      )}

      {/* ── Barra inserimento rapido ── */}
      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" mb={1}>
          Inserimento rapido — digita numero maglia, Tab o Enter per avanzare
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {Array.from({ length: 6 }, (_, i) => {
            const pos = i + 1;
            const hasError = !!quickErrors[i];
            const fp = positionMap[pos];
            const assignedPlayer = fp ? callup.find((p) => p.id === fp.playerId) : null;
            return (
              <Box key={pos} sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                  Pos {pos}
                </Typography>
                <Tooltip title={quickErrors[i] ?? (assignedPlayer ? `${assignedPlayer.firstName} ${assignedPlayer.lastName}` : '')} arrow>
                  <TextField
                    inputRef={(el) => { inputRefs.current[i] = el; }}
                    value={quickInputs[i]}
                    onChange={(e) => setQuickInputs((prev) => {
                      const n = [...prev]; n[i] = e.target.value; return n;
                    })}
                    onBlur={() => commitQuickInput(i, quickInputs[i])}
                    onKeyDown={(e) => handleQuickKeyDown(e, i)}
                    error={hasError}
                    size="small"
                    placeholder="—"
                    inputProps={{ maxLength: 3, style: { textAlign: 'center', padding: '6px 4px' } }}
                    sx={{
                      width: 56,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: assignedPlayer ? `${shirtColor}18` : 'background.paper',
                        borderColor: assignedPlayer ? shirtColor : undefined,
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* ── Schema P1-P6 + Ruota ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" mb={0.75}>
          Schema di rotazione (palleggio in zona…)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup value={schema} exclusive onChange={handleSchemaChange} size="small">
            {[1, 2, 3, 4, 5, 6].map((p) => (
              <ToggleButton key={p} value={p} sx={{ minWidth: 40 }}>P{p}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title={isComplete ? 'Ruota tutti i giocatori di una posizione in senso antiorario' : 'Schiera tutti e 6 i giocatori e seleziona lo schema per abilitare la rotazione'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                disabled={!isComplete}
                onClick={handleRotate}
                startIcon={<RotateRightIcon />}
              >
                Ruota
              </Button>
            </span>
          </Tooltip>
        </Box>
        {schema !== null && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Palleggio in pos {schema} · Opposto in pos {schema <= 3 ? schema + 3 : schema - 3}
          </Typography>
        )}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">

        {/* ── Campo visivo ── */}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5} textAlign="center">
            ← RETE →
          </Typography>
          <Box sx={{ border: '2px solid', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: '#2e7d3215', display: 'inline-block' }}>
            {FIELD_LAYOUT.map((row, ri) => (
              <Stack direction="row" key={ri} spacing={1} mb={ri === 0 ? 1 : 0}>
                {row.map((pos) => {
                  const fp          = positionMap[pos];
                  const player      = fp ? callup.find((p) => p.id === fp.playerId) : null;
                  const isEmpty     = !fp;
                  const isLiberoSlot = fp?.isLibero ?? false;
                  const roleLabel   = fp && schema && !isLiberoSlot ? ROTATION_SCHEMA[schema][pos] : null;

                  return (
                    <Box
                      key={pos}
                      onClick={() => handlePositionClick(pos)}
                      sx={{
                        position: 'relative',
                        width: 82, height: 78,
                        border: '1px dashed',
                        borderColor: isLiberoSlot ? '#f9a825'
                          : isEmpty ? (selected ? 'primary.main' : 'grey.400')
                          : shirtColor,
                        borderRadius: 1,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        cursor: selected ? 'crosshair' : 'default',
                        bgcolor: isLiberoSlot ? '#fff9c4'
                          : isEmpty ? 'transparent'
                          : `${shirtColor}22`,
                        '&:hover': {
                          bgcolor: selected ? `${shirtColor}44`
                            : isLiberoSlot ? '#fff176'
                            : isEmpty ? 'action.hover'
                            : `${shirtColor}33`,
                        },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {pos}
                      </Typography>
                      {player ? (
                        <>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: isLiberoSlot ? '#f9a825' : shirtColor, fontSize: 11, color: isLiberoSlot ? '#000' : undefined }}>
                            {player.shirtNumber || '?'}
                          </Avatar>
                          <Typography variant="caption" noWrap sx={{ maxWidth: 76, fontSize: 10 }}>
                            {player.lastName}
                          </Typography>
                          {roleLabel && (
                            <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1 }}>{roleLabel}</Typography>
                          )}
                          {isLiberoSlot && (
                            <Typography variant="caption" sx={{ fontSize: 9, color: '#e65100', fontWeight: 'bold', lineHeight: 1 }}>LIBERO</Typography>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => handleRemove(pos, e)}
                            sx={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'error.light', color: 'white' } }}
                          >
                            <CloseIcon sx={{ fontSize: 11 }} />
                          </IconButton>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                          {selected ? 'tocca' : 'vuoto'}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            ))}
          </Box>
        </Box>

        {/* ── Panchina ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" mb={1}>
            Panchina ({available.length} disponibili)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            {available.map((player) => {
              const isSelected = selected?.id === player.id;
              const isLibero   = player.id === designatedLiberoId;
              return (
                <Box
                  key={player.id}
                  onClick={() => setSelected(isSelected ? null : player)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    p: 0.75, borderRadius: 1, cursor: 'pointer',
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : isLibero ? '#f9a825' : 'divider',
                    bgcolor:     isSelected ? 'primary.50'   : isLibero ? '#fff9c4' : 'background.paper',
                    '&:hover': { bgcolor: isSelected ? 'primary.100' : 'action.hover' },
                  }}
                >
                  <Avatar sx={{ width: 28, height: 28, bgcolor: isLibero ? '#f9a825' : shirtColor, fontSize: 11, color: isLibero ? '#000' : undefined, flexShrink: 0 }}>
                    {player.shirtNumber || '?'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{player.firstName} {player.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">#{player.shirtNumber || '—'}</Typography>
                  </Box>
                  <Tooltip title={isLibero ? 'Rimuovi designazione libero' : 'Designa come libero'}>
                    <Chip
                      label="L" size="small"
                      onClick={(e) => toggleLibero(player, e)}
                      variant={isLibero ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: 10, fontWeight: 'bold', bgcolor: isLibero ? '#f9a825' : undefined, color: isLibero ? '#000' : 'text.secondary', borderColor: isLibero ? '#f9a825' : undefined, flexShrink: 0 }}
                    />
                  </Tooltip>
                </Box>
              );
            })}

            {/* Libero già in campo — mostrare per consentire revoca */}
            {lineup.filter((fp) => fp.isLibero).map((fp) => {
              const player = callup.find((p) => p.id === fp.playerId);
              if (!player) return null;
              return (
                <Box key={player.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1, border: '1px solid #f9a825', bgcolor: '#fff9c4', opacity: 0.7 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: '#f9a825', fontSize: 11, color: '#000', flexShrink: 0 }}>
                    {player.shirtNumber || '?'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{player.firstName} {player.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">#{player.shirtNumber || '—'} · in campo</Typography>
                  </Box>
                  <Tooltip title="Rimuovi designazione libero">
                    <Chip label="L" size="small" onClick={(e) => toggleLibero(player, e)} sx={{ height: 20, fontSize: 10, fontWeight: 'bold', bgcolor: '#f9a825', color: '#000', flexShrink: 0 }} />
                  </Tooltip>
                </Box>
              );
            })}

            {available.length === 0 && lineup.filter((fp) => fp.isLibero).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ gridColumn: 'span 2' }}>Tutti schierati</Typography>
            )}
          </Box>
        </Box>
      </Stack>

      </Box>

      {/* ── Navigazione ── */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button onClick={onBack} color="inherit">← Indietro</Button>
          <Tooltip title={isComplete ? '' : lineup.length < 6 ? 'Schiera tutti e 6 i giocatori' : 'Seleziona lo schema di rotazione'}>
            <span>
              <Button variant="contained" disabled={!isComplete} onClick={onConfirm}>
                {side === 'HOME' ? `Avanti → ${nextTeamName}` : 'Inizia partita →'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
}
